import {
  fetchWithCache,
  generateUserCacheKey,
  invalidateCache,
} from "@punchup/utils/caches";

import { Database } from "@punchup/data/supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { getUser } from "../supabase/supabase";
import { logException } from "@punchup/sentry";

// For use by Front-end
export async function getAuthorizedComediansAndVenuesFrontEnd(
  supabase: SupabaseClient<Database>
): Promise<{ comedians: string[]; venues: string[] }> {
  const user = await getUser(supabase);
  if (!user) {
    return { comedians: [], venues: [] };
  }

  const response = await fetch("/api/user-status", {
    method: "GET",
  });

  const { error, data } = await response.json();

  if (!error && data) {
    return data;
  }

  return { comedians: [], venues: [] };
}

export async function getAuthorizedComedianIdsFromCache(
  supabase: SupabaseClient<Database>
): Promise<string[]> {
  const user = await getUser(supabase);
  if (!user) {
    return [];
  }
  const cacheKey = generateUserCacheKey(user.id, {
    authorizedComedianIds: true,
  });
  return await fetchWithCache(
    cacheKey,
    () => getAuthorizedComedianIdsServer(supabase),
    60 * 60
  );
}

export async function clearUserAuthorizedComedianIdsCache(userId: string) {
  const cacheKey = generateUserCacheKey(userId, {
    authorizedComedianIds: true,
  });
  await invalidateCache(cacheKey);
}

// For use by Backend (routes)
export async function getAuthorizedComedianIdsServer(
  supabase: SupabaseClient<Database>
): Promise<string[]> {
  const user = await getUser(supabase);
  if (!user) {
    return [];
  }

  const { data, status, error } = await supabase
    .from("comedian_dashboard_admins")
    .select()
    .eq("admin_id", user.id);

  if (error || status !== 200) {
    logException(error, {
      data:
        "An error occurred while fetching users with special permissions, status: " +
        status,
    });
  }
  return (data || []).map((adminrow) => adminrow.comedian_id);
}

export async function getIsUserAuthorizedToManageComedian(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<boolean> {
  const authorizedComedianIds = await getAuthorizedComedianIdsServer(supabase);
  return authorizedComedianIds.includes(comedianId);
}

// For use by Backend (routes)
export async function getAuthorizedVenuePagesIdsServer(
  supabase: SupabaseClient<Database>
): Promise<string[]> {
  const user = await getUser(supabase);
  if (!user) {
    return [];
  }

  const { data, status, error } = await supabase
    .from("venue_pages_admins")
    .select()
    .eq("admin_id", user.id);

  if (error || status !== 200) {
    logException(error, {
      data:
        "An error occurred while fetching users with special permissions, status: " +
        status,
    });
  }
  return (data || []).map((adminrow) => adminrow.venue_page_id);
}
